import type { LineItem } from 'tsconfig/types';

/**
 * Checks if a line item has a bulk deal discount applied
 * @param item - The line item to check for bulk deal discount
 * @param discountName - The name of the discount to search for
 * @returns Boolean indicating if the item has specified bulk deal discount
 *
 * @example
 * ```typescript
 * const hasDiscount = hasBulkDealDiscount(lineItem, "bulk10");
 * Returns true if lineItem has a "bulk10" discount applied
 * ```
 */
export const hasBulkDealDiscount = (item: LineItem | undefined, discountName: string): boolean =>
  item?.discountedPricePerQuantity?.some(dpq =>
    dpq?.discountedPrice?.includedDiscounts?.some(discount =>
      discount?.discount?.name?.toLowerCase().includes(discountName.toLocaleLowerCase()),
    ),
  ) ?? false;

type SavingsSummary = { totalSavings: number; unlimitedSavings: number; upsellSavings: number };

const isPresellOrPreRelease = (item: LineItem) =>
  item?.product?.skus?.some(sku => sku?.preSellItem || sku?.preReleaseItem);

const calculateItemTotalSavings = (item: LineItem): number => {
  const baseSavings = item?.savings?.totalCartDiscountSavings || 0;
  const additionalSavings = item?.savings?.totalProductDiscountSavings || 0;
  return isPresellOrPreRelease(item) ? baseSavings : baseSavings + additionalSavings;
};

const calculateUpsellSavings = (item: LineItem): number => {
  if (!item?.discountedPricePerQuantity) {
    return 0;
  }
  return item.discountedPricePerQuantity.reduce((sum, dpq) => {
    const discountName = dpq?.discountedPrice?.includedDiscounts?.[0]?.discount?.name?.toLowerCase();
    if (discountName?.includes('subscribesave')) {
      return sum + (dpq?.discountedPrice?.includedDiscounts?.[0]?.discountedAmount?.amount * dpq?.quantity || 0);
    }
    return sum;
  }, 0);
};

export const calculateOrderSummarySavings = (lineItems: LineItem[]): SavingsSummary => {
  let totalSavings = 0;
  let unlimitedSavings = 0;
  let upsellSavings = 0;

  for (const item of lineItems || []) {
    if (!item?.itemClassifications?.length) {
      totalSavings += calculateItemTotalSavings(item);
    }

    if (item?.unitPrice?.priceType === 'mpp') {
      unlimitedSavings += item?.savings?.totalProductDiscountSavings || 0;
    }

    upsellSavings += calculateUpsellSavings(item);
  }

  return {
    totalSavings: Math.round((totalSavings - unlimitedSavings - upsellSavings) * 100) / 100,
    unlimitedSavings: Math.round(unlimitedSavings * 100) / 100,
    upsellSavings: Math.round(upsellSavings * 100) / 100,
  };
};
